import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { AuthContext } from "./AuthContext";
import * as authApi from "../api/auth";
import type { User } from "../api/auth";
import { setUserId } from "../telemetry/init";
import { ADMIN_EMAIL_DOMAIN } from "../lib/constants";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const verifiedRef = useRef(false);

  useEffect(() => {
    authApi
      .getMe()
      .then((res) => {
        setUser(res.user);
        setUserId(res.user.id);
      })
      .catch(() => {
        if (!verifiedRef.current) {
          setUser(null);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (email: string, turnstileToken?: string) => {
    await authApi.login(email, turnstileToken);
  }, []);

  const verifyToken = useCallback(async (token: string) => {
    const res = await authApi.verify(token);
    verifiedRef.current = true;
    setUser(res.user);
    setUserId(res.user.id);
    setIsLoading(false);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  const isAdmin =
    user?.email.toLowerCase().endsWith(ADMIN_EMAIL_DOMAIN) ?? false;

  return (
    <AuthContext value={{ user, isLoading, isAdmin, login, verifyToken, logout }}>
      {children}
    </AuthContext>
  );
}
